import { Grid, Box, Typography, Divider, IconButton, Card, Collapse, Tab, Tabs, Button, Menu, MenuItem, useMediaQuery, } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { alpha, darken, lighten } from '@mui/material/styles';
import { ChangeEvent, Dispatch, FC, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import AdditionalActions from './helpers/AdditionalActions';
import TooltipField from 'src/shared/support/TooltipField';
import { separateMenuComponents } from 'src/core/ui/helpers/separateMenuComponents';
import UnfoldMoreTwoToneIcon from '@mui/icons-material/UnfoldMoreTwoTone';
import randomId from 'src/shared/helpers/randomId';
import { CardContext } from "src/core/ui/CardContext";
import { ErrorBoundary } from './helpers/ErrorBoundry';
import type { customComponent } from 'src/core/ui/PageHeader';
import CustomButton from './CustomButton';

export const ContentCard = ({ cardId = "cardID-" + randomId(), content, cardHeader: cardTitleText = cardId, cardSubTitle, cardTypes, tooltipText, contentPadding = 2, formLayout = false, xs = 12, sm, md = 12, lg, xl, components, children, overriddenCard }: ContentCardProps) => {
  const { cards } = useContext(CardContext);
  const card = cards.find(c => c.id === cardId);
  const isCollapsable = cardTypes?.some(cardType => cardType == CardType.collapsable) ?? false;

  const [collapsed, toggleExpanded] = useState<boolean>(isCollapsable && !cardTypes?.includes(CardType.visibleInitally));
  const handleCollapseClick = () => toggleExpanded(!collapsed);
  const hideable = cardTypes?.some(cardType => cardType == CardType.hideable) ?? false;
  const isCardVisible = card?.isVisible ?? true;
  const isTabbed = cardTypes?.some(cardType => cardType == CardType.tab) ?? false;
  const isHighlighted = cardTypes?.some(cardType => cardType == CardType.highlight) ?? false;

  return (
    <Grid item xs={xs} sm={sm} md={md} lg={lg} xl={xl} className={cardId} sx={{ display: isCardVisible ? 'flex' : 'none', flexDirection: 'column', minWidth: 0, alignSelf: isCollapsable && collapsed ? 'flex-start' : 'stretch', }} pt={0} pb={0}>
      <Collapse in={isCardVisible} timeout="auto" unmountOnExit sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: 1, /* Only stretch the visibility wrappers. The content Collapse below */ /* retains its natural height and its normal expansion animation. */ '& > .MuiCollapse-wrapper': { flex: 1, width: 1, minWidth: 0 }, '& > .MuiCollapse-wrapper > .MuiCollapse-wrapperInner': { display: 'flex', flexDirection: 'column', width: 1, minWidth: 0, }, }}>
        <Box pb={isTabbed ? 0 : 2} sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          {
            overriddenCard ? <>{overriddenCard}</> : (
              <Card sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, width: 1, ...(isHighlighted ? { backgroundColor: '#b2c0ab' } : {}), }}>
                <ContentCardHeader {...{ cardId, cardHeader: (cardTitleText), cardSubTitle, cardTypes, tooltipText, components }} handleCollapseClick={handleCollapseClick} cardId={cardId} collapsable={isCollapsable} hideable={hideable} collapsed={collapsed} />
                <Collapse in={!collapsed} timeout="auto" unmountOnExit>
                  <Grid sx={{ width: 1 }} container pt={contentPadding}>
                    <Box sx={{ width: 1, ...(formLayout ? { containerType: 'inline-size', containerName: 'bold-card-fields' } : {}) }} px={contentPadding} pb={contentPadding == 0 ? 0 : contentPadding + 1}>
                      <Grid container direction="row" columnSpacing={formLayout ? 2 : 0} justifyContent={formLayout ? "flex-start" : "space-between"} alignContent={'stretch'} alignItems="stretch">
                        <CardFieldLayoutContext.Provider value={formLayout}>
                          <ErrorBoundary>
                            {content}
                            {children}
                          </ErrorBoundary>
                        </CardFieldLayoutContext.Provider>
                      </Grid>
                    </Box>
                  </Grid>
                </Collapse>
              </Card>
            )
          }
        </Box>
      </Collapse>
    </Grid>
  );
};

// One place to tune the surfaces used by cards and outlined form controls.
// Editability comes from the control's real disabled/readOnly state.
export function getFormSurfaces(theme: Theme) {
  const dark = theme.palette.mode === 'dark';
  const card = dark ? lighten(theme.palette.background.paper, 0.065) : theme.palette.background.paper;
  return { card, editable: darken(card, dark ? 0.2 : 0.025), inactive: dark ? lighten(card, 0.02) : darken(card, 0.008), };
}

export const cardSurfaceStyles = ({ theme }: { theme: Theme }) => ({ backgroundColor: getFormSurfaces(theme).card, backgroundImage: 'none', });

export const outlinedFieldSurfaceStyles = (theme: Theme) => ({ backgroundColor: getFormSurfaces(theme).editable, '&.Mui-disabled, &.Mui-readOnly, &.MuiInputBase-readOnly': { backgroundColor: getFormSurfaces(theme).inactive, }, });

export enum CardType {
  noHeader,
  collapsable,
  hideable,
  visibleInitally,
  tab,
  highlight,
  tabContainer,
}

export const ContentCardHeader = ({ cardId = "", cardHeader: cardTitleText = null, cardSubTitle = null, tooltipText = null, cardTypes = [], handleCollapseClick = () => { }, hideable = false, collapsable = false, collapsed = false, components = null, }: ContentCardHeaderProps) => {
  const { isCardVisible } = useContext(CardContext);
  const isTabContainer = cardTypes.includes(CardType.tabContainer);
  if (!cardTypes?.some(cardType => cardType == CardType.noHeader) && !cardTypes?.some(cardType => cardType == CardType.tab)) {
    const [TopRightComponents, MenuItemsComponents] = separateMenuComponents(components);

    return (
      <>
        <Box px={2} py={2} display="flex" justifyContent="space-between" sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 0 }}>
          <Box sx={{ minWidth: 0, maxWidth: 1, flex: isTabContainer ? '1 1 240px' : '1 1 auto' }}>
            <Typography gutterBottom={!isTabContainer} variant="h4" component={isTabContainer ? 'div' : 'h4'}> {cardTitleText} <TooltipField tooltipText={tooltipText}></TooltipField> </Typography>
            <Typography variant="subtitle2">  {cardSubTitle} </Typography>
          </Box>
          <Box display="flex" justifyContent="flex-end" sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0, maxWidth: 1 }}>
            {TopRightComponents?.map((customComponent, i) => (<Grid pl={1} key={i} item>{customComponent.component}</Grid>))}
            <Grid item>
              <CollapseButton collapsable={collapsable} handleCollapseClick={handleCollapseClick} collapsed={collapsed} />
              <HideButton cardId={cardId} hiddenCard={hideable} hidden={!isCardVisible(cardId)} />
            </Grid>
            <AdditionalActions>{MenuItemsComponents?.length > 0 ? MenuItemsComponents?.map((customComponent, i) => (<Grid key={i} item>{customComponent.component}</Grid>)) : null}</AdditionalActions>
          </Box>
        </Box>
        <Divider />
      </>);
  }
  return (<></>);
};

const CollapseButton = ({ collapsable, handleCollapseClick, collapsed }: { collapsable: Boolean, handleCollapseClick: Function, collapsed: Boolean }) => {
  // This disclosure owns aria-expanded; CustomButton does not forward that widget contract.
  if (collapsable) {
    return (
      <IconButton color="primary" aria-label={collapsed ? 'Expand card' : 'Collapse card'} aria-expanded={!collapsed} onClick={() => handleCollapseClick()}>
        <ExpandMoreTwoToneIcon sx={{ transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)' }} />
      </IconButton>
    );
  } return (<></>);
};

const HideButton = ({ cardId, hiddenCard, hidden }: { cardId: string, hiddenCard: Boolean, hidden: Boolean }) => {
  const { hideCard } = useContext(CardContext); // Import the context
  if (hiddenCard) {
    return (
      <CustomButton title="Hide card" tooltipText="Hide card" iconButton icon={<CloseIcon sx={{ transform: hidden ? 'rotate(0deg)' : 'rotate(180deg)' }} />} onClick={() => hideCard(cardId)} />
    );
  }
  return (<></>);
};

export const ContentCardsContainer = ({ contentCards, children }: { contentCards?: ContentCardProps[], children?}) => {
  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, minWidth: 0 }}>
      <Grid container direction="row" justifyContent="flex-start" alignContent={'stretch'} alignItems="stretch" rowSpacing={0} columnSpacing={2} pt={0}>
        {contentCards?.map((contentCard) => (<ContentCard key={contentCard.cardId} {...contentCard} />))}
        {children}
      </Grid>
    </Box>
  );
};

export default ContentCardsContainer;

export const CardSectionHeader: FC<{ contentCard?: ContentCardProps, children?}> = ({ contentCard, children }) => {
  return (
    <>
      <Grid item xs={12} pb={0}>
        <Box pb={4}>
          <Typography gutterBottom variant="h3"> {contentCard.cardHeader ?? contentCard.cardId} </Typography>
          <Typography variant="subtitle2">  {contentCard.cardSubTitle} </Typography>
          <ErrorBoundary>{children}</ErrorBoundary>
        </Box>
      </Grid>
    </>
  );
};

// Opt in at the card or section level; existing form pages keep their layout.
export const CardFieldLayoutContext = React.createContext(false);

export const CardTitle = ({ icon, children }: { icon?: React.ReactNode; children: React.ReactNode }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
    {icon && <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: 18 } }}>{icon}</Box>}
    {children}
  </Box>
);

export const CardSection = ({ title, children, divider = false }: CardSectionProps) => (
  <Grid item xs={12} component="section" aria-label={typeof title === 'string' ? title : undefined} sx={{ minWidth: 0, containerType: 'inline-size', containerName: 'bold-card-fields', '&:not(:first-child)': { mt: 1 } }}>
    {title && <Typography component="h5" variant="overline" sx={{ display: 'block', color: 'text.secondary', fontSize: '12px', lineHeight: 1.5, textTransform: 'uppercase', fontWeight: 500, letterSpacing: '.5px', mb: 0.5 }}>{title}</Typography>}
    <CardFieldLayoutContext.Provider value={true}>
      <Grid container columnSpacing={2} alignItems="flex-start">{children}</Grid>
    </CardFieldLayoutContext.Provider>
    {divider && <Divider sx={{ mt: 1, mb: 2, bgcolor: theme => alpha(theme.palette.text.primary, 0.16) }} />}
  </Grid>
);

export const HeaderTabs = ({ tabs, currentTab, setCurrentTab }: headerTabsProps) => {
  const handleTabsChange = (_event: ChangeEvent<{}>, value: string): void => {
    setCurrentTab(value);
  };
  return (
    <Box sx={{ minWidth: 0, maxWidth: 1 }}>
      <Tabs onChange={handleTabsChange} value={currentTab ?? false} variant="scrollable" scrollButtons="auto" textColor="primary" indicatorColor="primary" sx={{ display: { xs: 'none', sm: 'flex' }, minWidth: 0, maxWidth: 1, height: 'auto', minHeight: 38, overflow: 'hidden', '& .MuiTabs-scroller.MuiTabs-scrollableX': { overflowX: 'auto !important', overflowY: 'hidden' }, /* The selected tab owns its rounded surface and inset underline, so */ /* neither depends on the themes' sliding/full-height indicator. */ '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTab-root.MuiButtonBase-root': { flexShrink: 0, padding: '10px 12px', mr: '6px', borderRadius: '6px', height: 38, minHeight: 38, minWidth: 0, fontSize: '14px', fontWeight: 400, lineHeight: '18px', letterSpacing: 0, color: 'text.secondary', textTransform: 'none', '&:hover': { color: 'text.primary', bgcolor: 'action.hover' }, '&.Mui-selected, &.Mui-selected:hover': { color: 'primary.main', bgcolor: theme => getFormSurfaces(theme).editable, boxShadow: theme => `inset 0 -2px ${theme.palette.primary.main}`, }, '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' }, }, }}>
        {tabs.map((tab) => (<Tab key={tab.value} label={tab.label} value={tab.value} />))}
      </Tabs>
      <MobileTabs {...{ tabs, currentTab, setCurrentTab }} />
    </Box>);
};

const MobileTabs = ({ tabs, currentTab, setCurrentTab }: headerTabsProps) => {
  const actionRef = useRef<any>(null);
  const [openLocation, setOpenMenuLocation] = useState<boolean>(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  useEffect(() => {
    if (!isMobile) setOpenMenuLocation(false);
  }, [isMobile]);
  return (
    <>
      <Button endIcon={<UnfoldMoreTwoToneIcon />} ref={actionRef} onClick={() => setOpenMenuLocation(true)} variant="text" aria-haspopup="menu" aria-expanded={openLocation} sx={{ display: { sm: 'none', xs: 'flex' }, maxWidth: 1, color: 'primary.main', bgcolor: theme => getFormSurfaces(theme).editable, boxShadow: theme => `inset 0 -2px ${theme.palette.primary.main}`, borderRadius: '6px', padding: '10px 12px', minHeight: 38, fontSize: '14px', fontWeight: 400, lineHeight: '18px', letterSpacing: 0, textTransform: 'none' }}>
        {tabs.find(tab => tab.value === currentTab)?.label ?? currentTab}
      </Button>
      <Menu disableScrollLock /* Popover also repositions during resize, before matchMedia has always */ /* rerendered this component. Never anchor to a newly hidden button. */ anchorEl={() => actionRef.current?.getClientRects().length ? actionRef.current : document.body} onClose={() => setOpenMenuLocation(false)} open={isMobile && openLocation} anchorOrigin={{ vertical: 'center', horizontal: 'center' }} transformOrigin={{ vertical: 'center', horizontal: 'center' }}>
        {tabs.map((_tab) => (<MenuItem key={_tab.value} selected={_tab.value === currentTab} onClick={() => { setCurrentTab(_tab.value ?? ""); setOpenMenuLocation(false); }}>{_tab.label}</MenuItem>))}
      </Menu></>
  );
};

export interface ContentCardProps {
  cardId?: string;
  content?: any;
  cardHeader?: string | React.ReactNode;
  cardSubTitle?: string | React.ReactNode;
  cardTypes?: CardType[];
  components?: customComponent[];
  contentPadding?: number;
  /** Use even field gutters/action space without requiring inner sections. */
  formLayout?: boolean;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  tooltipText?: string;
  children?: React.ReactNode | React.ReactElement<unknown, string | React.JSXElementConstructor<any>> | React.ReactElement<unknown, string | React.JSXElementConstructor<any>>[];
  overriddenCard?: React.ReactNode;
}

interface ContentCardHeaderProps extends ContentCardProps {
  cardId: string,
  handleCollapseClick?: Function,
  hideable?: boolean,
  collapsable?: boolean,
  collapsed?: Boolean,
}

export interface CardSectionProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Add a divider after this section, never before its heading. */
  divider?: boolean;
}

export interface CardTab {
  value: string,
  label: string,
}

interface headerTabsProps {
  tabs: CardTab[],
  currentTab: string,
  setCurrentTab: Dispatch<SetStateAction<string>>
}
